<script setup lang="ts">
import { ElInput } from 'element-plus'
import { computed, useSlots } from 'vue'

import { BoxComponent, ButtonComponent } from '..'
import RemoveIcon from '../../assets/icons/remove.svg?component'

const props = withDefaults(
  defineProps<{
    type?: string
    /** Forwarded by ElInput onto the native input, so number fields can set their bounds. */
    min?: number
    max?: number
    rows?: number
    placeholder?: string
    size?: 'default' | 'small' | 'large'
    maxlength?: string
    name?: string
    isDisabled?: boolean
    isClearable?: boolean
  }>(),
  {
    type: 'text',
    min: undefined,
    max: undefined,
    rows: 1,
    placeholder: '',
    size: 'default',
    maxlength: '',
    name: undefined,
    isDisabled: false,
    isClearable: false,
  },
)
const value = defineModel<string | number | null>('modelValue', { default: '' })
defineSlots<{ header?(): void; suffix?(): void; prefix?(): void }>()
const emits = defineEmits(['change', 'input', 'clear'])
const slots = useSlots()

const showClear = computed(() => props.isClearable && value.value !== '')

const clear = () => {
  value.value = ''
  emits('input', '')
  emits('clear')
}
</script>

<template>
  <div>
    <div v-if="slots.header">
      <BoxComponent :mb="8">
        <slot name="header" />
      </BoxComponent>
    </div>
    <ElInput
      v-bind="props"
      v-model="value"
      :name="name"
      :disabled="isDisabled"
      @change="(data: string) => $emit('change', data)"
      @input="(data: string) => $emit('input', data)"
    >
      <template v-if="slots.prefix" #prefix>
        <slot name="prefix" />
      </template>
      <template v-if="slots.suffix || isClearable" #suffix>
        <slot name="suffix" />
        <ButtonComponent v-if="showClear" variant="text" is-icon :icon-size="18" aria-label="Clear" class="input__clear" @click="clear">
          <RemoveIcon />
        </ButtonComponent>
      </template>
    </ElInput>
  </div>
</template>

<style scoped lang="scss">
:deep(.el-input__prefix),
:deep(.el-input__suffix) {
  color: var(--primary);
}

.button.input__clear {
  width: 32px;
  height: 32px;
  min-height: 32px;
  margin-right: -7px;
  padding: 8px;
  border-radius: 48px;
}

:deep(.el-input__wrapper) {
  height: 56px;
  padding: 16px;
  border-radius: 8px;
  border: none;
  font-size: 16px;
  background: var(--inverted);
  box-shadow: 0 0 0 1px var(--secondary-12);

  &:hover,
  &.is-focused {
    box-shadow: 0 0 0 1px var(--secondary-64);
  }
}

:deep(.el-input--small .el-input__wrapper) {
  height: 40px;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 24px;
}

:deep(.el-textarea__inner) {
  padding: 16px;
  border-radius: 8px;
  border: none;
  background: var(--inverted);
  box-shadow: 0 0 0 1px var(--secondary-12);

  &:hover,
  &.is-focused {
    box-shadow: 0 0 0 1px var(--secondary-64);
  }
}
</style>
